import { type CSSProperties, type ReactNode, useEffect, useMemo, useState } from 'react'
import { SEED_DESTINATIONS, SEED_ORIGINS } from '../data/seedPorts'
import { carrierBrand } from '../lib/carriers'
import {
  flagFor,
  setBox,
  setCols,
  setHiddenLiners,
  setLaneSort,
  toggleFav,
  togglePinned,
  useBox,
  useCols,
  useFavs,
  useHiddenLiners,
  useLaneSort,
  usePinned,
} from '../lib/personal'
import type { FavLane } from '../lib/personal'
import {
  type HistoryPoint,
  type LaneReliability,
  type LaneRow,
  type Sailing,
  fmtPrice,
  getLaneHistory,
  getLaneReliability,
  getLaneSailings,
  getLanes,
} from '../lib/rates'
import { type WatchState, watchLane } from '../lib/spot'
import {
  BOXES,
  BOX_IDS,
  type Box,
  CLOSE_BOX_RATIO,
  COLUMNS,
  CSV_HEADER,
  type CarrierOffer,
  type ColumnId,
  type DayValue,
  type LaneMetrics,
  type LaneSummary,
  MIN_DAY_SAMPLE,
  type OnTime,
  PORT_ALIASES,
  SORTS,
  THIN_SAMPLE,
  agoLabel,
  basketIndex,
  boxColumn,
  boxPrice,
  boxRatio,
  buildPortOptions,
  canonicalPort,
  displayPort,
  indexChange,
  laneCsvRows,
  offersByCarrier,
  onTimeByCarrier,
  portLabel,
  resolvePort,
  shortDate,
  sortLanes,
  summarize,
  toBox,
  toColumns,
  toCsv,
  toHidden,
  toSort,
  transshipPorts,
  trendPct,
} from '../lib/workspace-rates'
import { SpotPanel } from './SpotPanel'
import { UsagePanel } from './UsagePanel'

// "My lanes": the lanes a customer chose, with every liner's rate side by side. It runs in two places that
// do not share a stylesheet: inside the Navo24 workspace shell (module federation, host design tokens) and
// on freightratesmcp.com/dashboard (this site's own tokens). So nothing here uses Tailwind. Every colour is
// a --wr-* property defined once on the root, taking the host token when present and this site's token
// otherwise, and reads correctly in light and dark in both.
//
// The customer decides what the board shows: which container ranks the liners, which price columns are on,
// which liners are hidden, which lanes are pinned and how the lanes are ordered. All of it is remembered in
// this browser (lib/personal.ts).
//
// The rates are indicative market rates harvested from public liner cards, not a firm quote. The footer
// says so; a firm quote comes through the booking request.

const mono = "var(--font-mono, ui-monospace, 'SF Mono', Menlo, monospace)"

const tokens = {
  '--wr-card': 'var(--surface-0, rgb(var(--surface-0-rgb, 255 255 255)))',
  '--wr-sub': 'var(--surface-1, rgb(var(--surface-1-rgb, 238 241 244)))',
  '--wr-line': 'var(--border, rgb(var(--border-rgb, 14 20 27) / 0.14))',
  '--wr-ink': 'var(--text-primary, rgb(var(--ink-rgb, 11 12 14)))',
  '--wr-ink2': 'var(--text-secondary, rgb(var(--muted-rgb, 82 94 107)))',
  '--wr-muted': 'var(--text-muted, rgb(var(--muted-rgb, 82 94 107)))',
  '--wr-signal': 'var(--signal, rgb(var(--link-rgb, 31 79 230)))',
  '--wr-on': 'var(--on-accent, #fff)',
  '--wr-good': 'var(--up, rgb(var(--up-rgb, 22 163 74)))',
  '--wr-bad': 'var(--danger, rgb(var(--down-rgb, 220 38 38)))',
  '--wr-shadow': 'var(--shadow-card, var(--shadow, none))',
} as CSSProperties

const card: CSSProperties = {
  background: 'var(--wr-card)',
  border: '1px solid var(--wr-line)',
  borderRadius: 16,
  boxShadow: 'var(--wr-shadow)',
}
const label: CSSProperties = {
  fontFamily: mono,
  fontSize: 10,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: 'var(--wr-muted)',
}
const btn: CSSProperties = {
  font: 'inherit',
  fontSize: 13,
  fontWeight: 600,
  borderRadius: 10,
  padding: '9px 14px',
  cursor: 'pointer',
  border: '1px solid var(--wr-line)',
  background: 'var(--wr-card)',
  color: 'var(--wr-ink)',
}
const btnPrimary: CSSProperties = {
  ...btn,
  background: 'var(--wr-signal)',
  borderColor: 'var(--wr-signal)',
  color: 'var(--wr-on)',
}
const input: CSSProperties = {
  font: 'inherit',
  fontSize: 14,
  padding: '9px 12px',
  borderRadius: 10,
  border: '1px solid var(--wr-line)',
  background: 'var(--wr-card)',
  color: 'var(--wr-ink)',
  width: '100%',
  boxSizing: 'border-box',
}
const th: CSSProperties = {
  ...label,
  fontWeight: 600,
  textAlign: 'left',
  padding: '9px 12px',
  borderBottom: '1px solid var(--wr-line)',
  whiteSpace: 'nowrap',
}
const td: CSSProperties = {
  padding: '11px 12px',
  borderBottom: '1px solid var(--wr-line)',
  fontSize: 14,
  color: 'var(--wr-ink)',
  verticalAlign: 'top',
}
const sr: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
}

const pct = (n: number) => `${n > 0 ? '+' : ''}${n.toFixed(1)}%`
const laneId = (l: Pick<FavLane, 'pol' | 'pod'>) => `${l.pol}-${l.pod}`.toUpperCase()
const laneName = (l: FavLane) =>
  `${displayPort(l.pol, l.polName)} → ${displayPort(l.pod, l.podName)}`
// A rate that rose is bad news for the shipper, one that fell is good news.
const trendColor = (n: number | null) =>
  n == null || Math.abs(n) < 0.05 ? 'var(--wr-muted)' : n > 0 ? 'var(--wr-bad)' : 'var(--wr-good)'

// Lanes already announced to the backend in this page load.
const watchedThisLoad = new Set<string>()

// ── Data ────────────────────────────────────────────────────────────────────────────────────────

type Load =
  | { state: 'loading' }
  | { state: 'error' }
  | {
      state: 'ready'
      sailings: Sailing[]
      history: HistoryPoint[]
      reliability: LaneReliability[]
    }

/** Loads every lane's sailings, history and reliability; a refresh (tick) reloads them all. */
function useLaneLoads(lanes: FavLane[], tick: number): Record<string, Load> {
  const [loads, setLoads] = useState<Record<string, Load>>({})
  const key = lanes.map(laneId).join(',')

  // biome-ignore lint/correctness/useExhaustiveDependencies: key stands for the lane list; tick is the refresh trigger
  useEffect(() => {
    let live = true
    for (const l of lanes) {
      const id = laneId(l)
      setLoads((cur) => ({ ...cur, [id]: { state: 'loading' } }))
      Promise.all([
        getLaneSailings(l.pol, l.pod),
        // Trend and on-time are extras: a failed read of either must not hide the rates.
        getLaneHistory(l.pol, l.pod).catch(() => [] as HistoryPoint[]),
        getLaneReliability(l.pol, l.pod).catch(() => [] as LaneReliability[]),
      ])
        .then(([s, history, reliability]) => {
          if (live)
            setLoads((cur) => ({
              ...cur,
              [id]: { state: 'ready', sailings: s.sailings, history, reliability },
            }))
        })
        .catch(() => live && setLoads((cur) => ({ ...cur, [id]: { state: 'error' } })))
    }
    return () => {
      live = false
    }
  }, [key, tick])

  return loads
}

interface LaneView {
  offers: CarrierOffer[]
  unpriced: number
  liners: string[]
  priced: Sailing[]
  series: DayValue[]
  sum: LaneSummary
  onTime: Map<string, OnTime>
  sailingsTotal: number
}

function laneView(load: Load, box: Box, hidden: ReadonlySet<string>): LaneView | null {
  if (load.state !== 'ready') return null
  const { offers, unpriced, liners } = offersByCarrier(load.sailings, box, hidden)
  const shown = new Set(offers.map((o) => o.carrier))
  const priced = load.sailings
    .filter((s) => shown.has((s.carrier ?? '').trim() || 'Unknown'))
    .filter((s) => BOX_IDS.some((b) => boxPrice(s, b) != null))
    .sort(
      (a, b) =>
        (boxPrice(a, box) ?? Number.POSITIVE_INFINITY) -
          (boxPrice(b, box) ?? Number.POSITIVE_INFINITY) || (a.etd ?? '').localeCompare(b.etd ?? '')
    )
  const series: DayValue[] = load.history.flatMap((p) => {
    const v = box === '20gp' ? p.median_20gp : box === '40hc' ? p.median_40hc : null
    return v != null && v > 0 && p.sample >= MIN_DAY_SAMPLE ? [{ day: p.day, value: v }] : []
  })
  return {
    offers,
    unpriced,
    liners,
    priced,
    series,
    sum: summarize(offers, load.sailings),
    onTime: onTimeByCarrier(load.reliability),
    sailingsTotal: load.sailings.length,
  }
}

// ── Pieces ──────────────────────────────────────────────────────────────────────────────────────

function CarrierChip({ code }: { code: string }) {
  const b = carrierBrand(code)
  const known = !b.bg.startsWith('rgb(')
  return (
    <span
      style={{
        display: 'inline-block',
        fontFamily: mono,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.04em',
        padding: '3px 8px',
        borderRadius: 6,
        background: known ? b.bg : 'var(--wr-sub)',
        color: known ? b.fg : 'var(--wr-ink)',
        border: known ? 'none' : '1px solid var(--wr-line)',
      }}
    >
      {code}
    </span>
  )
}

function Line({
  values,
  color,
  w,
  h,
  label: aria,
}: { values: number[]; color: string; w: number; h: number; label: string }) {
  if (values.length < 2) return null
  const mn = Math.min(...values)
  const mx = Math.max(...values)
  const r = mx - mn || 1
  const X = (i: number) => 2 + (i / (values.length - 1)) * (w - 4)
  const Y = (v: number) => h - 3 - ((v - mn) / r) * (h - 6)
  const d = values.map((v, i) => `${i ? 'L' : 'M'}${X(i).toFixed(1)},${Y(v).toFixed(1)}`).join(' ')
  return (
    <svg
      width="100%"
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={aria}
    >
      <path
        d={d}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  )
}

function Menu({ title, children }: { title: string; children: ReactNode }) {
  return (
    <details style={{ position: 'relative' }}>
      <summary style={{ ...btn, listStyle: 'none', display: 'inline-block' }}>{title} ▾</summary>
      <div
        style={{
          ...card,
          position: 'absolute',
          zIndex: 20,
          top: 'calc(100% + 6px)',
          left: 0,
          minWidth: 220,
          maxHeight: 340,
          overflowY: 'auto',
          padding: 12,
          display: 'grid',
          gap: 8,
        }}
      >
        {children}
      </div>
    </details>
  )
}

function Check({
  checked,
  onChange,
  children,
}: { checked: boolean; onChange: (v: boolean) => void; children: ReactNode }) {
  return (
    <label
      style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, cursor: 'pointer' }}
    >
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {children}
    </label>
  )
}

function IndexPanel({
  points,
  boxLabel,
  stats,
  noHistory,
}: {
  points: { day: string; index: number }[]
  boxLabel: string
  noHistory: boolean
  stats: {
    avg: number | null
    cheapest: string | null
    riser: string | null
    faller: string | null
    lanes: number
  }
}) {
  const change = indexChange(points)
  const last = points[points.length - 1]
  return (
    <section style={{ ...card, padding: 20, marginTop: 16 }} aria-label="My lanes index">
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'center' }}>
        <div style={{ minWidth: 150 }}>
          <div style={label}>My lanes index · {boxLabel}</div>
          {last ? (
            <>
              <div style={{ fontFamily: mono, fontSize: 34, fontWeight: 700, lineHeight: 1.1 }}>
                {last.index.toFixed(1)}
              </div>
              <div
                style={{
                  fontFamily: mono,
                  fontSize: 13,
                  fontWeight: 600,
                  color: trendColor(change),
                }}
              >
                {change == null ? '' : `${pct(change)} over ${points.length} days`}
              </div>
            </>
          ) : (
            <div style={{ fontSize: 13, color: 'var(--wr-ink2)', marginTop: 6, maxWidth: 220 }}>
              {noHistory
                ? "There is no rate history for 40' containers. Rank by 20' or 40' HC to see the index."
                : 'Not enough history on your lanes yet.'}
            </div>
          )}
        </div>
        <div style={{ flex: '1 1 260px', minWidth: 200 }}>
          <Line
            values={points.map((p) => p.index)}
            color={trendColor(change)}
            w={400}
            h={64}
            label="My lanes index"
          />
        </div>
        <div style={{ display: 'grid', gap: 6, fontSize: 13, minWidth: 200 }}>
          <div>
            <span style={label}>Average best </span>
            <b style={{ fontFamily: mono }}>{stats.avg == null ? '-' : fmtPrice(stats.avg)}</b>
          </div>
          <div>
            <span style={label}>Cheapest lane </span>
            <b>{stats.cheapest ?? '-'}</b>
          </div>
          <div>
            <span style={label}>Rising most </span>
            <b style={{ color: 'var(--wr-bad)' }}>{stats.riser ?? '-'}</b>
          </div>
          <div>
            <span style={label}>Falling most </span>
            <b style={{ color: 'var(--wr-good)' }}>{stats.faller ?? '-'}</b>
          </div>
        </div>
      </div>
      <p style={{ fontSize: 12, color: 'var(--wr-muted)', margin: '12px 0 0', lineHeight: 1.5 }}>
        An index of your {stats.lanes} {stats.lanes === 1 ? 'lane' : 'lanes'}: the daily median rate
        on each, averaged by day-on-day change, 100 on the first day. It follows the market on your
        lanes, so it moves when rates do, not when you add a lane.
      </p>
    </section>
  )
}

/**
 * The one number that tells a shipper what the choice of liner is worth on this lane, put where it is read
 * first: how far apart the cheapest and the dearest liner are, and who they are.
 */
function SpreadBand({ view, boxLabel }: { view: LaneView; boxLabel: string }) {
  const priced = view.offers.filter((o) => o.price != null)
  const lo = priced[0]
  const hi = priced[priced.length - 1]
  const spread = view.sum.spread
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 16,
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '14px 20px',
        background: 'var(--wr-sub)',
        borderBottom: '1px solid var(--wr-line)',
      }}
    >
      {spread != null && lo && hi ? (
        <div style={{ display: 'flex', gap: 14, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <span
            style={{ fontFamily: mono, fontSize: 26, fontWeight: 700, color: 'var(--wr-signal)' }}
          >
            {fmtPrice(spread)}
          </span>
          <span style={{ fontSize: 14, fontWeight: 600 }}>
            between the cheapest and the dearest liner ({boxLabel})
          </span>
          <span style={{ fontFamily: mono, fontSize: 12, color: 'var(--wr-ink2)' }}>
            {lo.carrier} {fmtPrice(lo.price)} → {hi.carrier} {fmtPrice(hi.price)}
          </span>
        </div>
      ) : (
        <span style={{ fontSize: 14, fontWeight: 600 }}>
          {priced.length === 1
            ? `One liner prices ${boxLabel} on this lane`
            : `No liner prices ${boxLabel} on this lane`}
        </span>
      )}
      <span style={{ fontSize: 12, color: 'var(--wr-ink2)' }}>
        {view.priced.length} priced sailings · {view.offers.length} liners
        {view.unpriced > 0 ? ` · ${view.unpriced} not priced` : ''}
      </span>
    </div>
  )
}

/**
 * The rates are the source's own listing prices, and on some lanes the source lists the 20' barely under the
 * 40'. Say so where the customer reads the prices, with the number, instead of letting them take the 20' as
 * a firm gap.
 */
function BoxRatioNote({ offers }: { offers: CarrierOffer[] }) {
  const r = boxRatio(offers)
  if (!r || r.ratio >= CLOSE_BOX_RATIO) return null
  return (
    <div
      role="note"
      style={{
        padding: '10px 20px',
        fontSize: 12,
        lineHeight: 1.5,
        color: 'var(--wr-ink2)',
        borderBottom: '1px solid var(--wr-line)',
      }}
    >
      <b style={{ color: 'var(--wr-ink)' }}>The 20' is priced close to the 40' here.</b> On this
      lane the 40' is listed at {r.ratio.toFixed(2)}x the 20' (median of {r.liners} liners). The
      spot-rate sources below show the same, so it is what the market quotes today, but a gap this
      small is unusual: confirm the 20' with the liner before you book.
    </div>
  )
}

function LaneCard({
  lane,
  box,
  load,
  view,
  cols,
  pinned,
  onPin,
  onRemove,
  watch,
  tick,
}: {
  lane: FavLane
  box: Box
  load: Load
  view: LaneView | null
  cols: ReadonlySet<ColumnId>
  pinned: boolean
  onPin: () => void
  onRemove: () => void
  watch: WatchState | null
  tick: number
}) {
  const [all, setAll] = useState(false)
  const polName = displayPort(lane.pol, lane.polName)
  const podName = displayPort(lane.pod, lane.podName)
  const change = view ? trendPct(view.series.map((p) => p.value)) : null
  const boxLabel = BOXES.find((b) => b.id === box)?.label ?? ''
  const fresh = view ? agoLabel(view.sum.updatedAt) : ''
  const hasOnTime =
    !!view &&
    cols.has('ontime') &&
    view.offers.some((o) => view.onTime.has(o.carrier.toUpperCase()))
  const priceCols = BOXES.filter((b) => cols.has(boxColumn(b.id)))
  const lowest = (b: Box) => {
    const v = view?.offers.map((o) => o.prices[b]).filter((x): x is number => x != null) ?? []
    return v.length ? Math.min(...v) : null
  }

  return (
    <section style={{ ...card, overflow: 'hidden' }} aria-label={`${polName} to ${podName}`}>
      <header
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 16,
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '18px 20px',
          borderBottom: '1px solid var(--wr-line)',
        }}
      >
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', minWidth: 0 }}>
          <button
            type="button"
            aria-pressed={pinned}
            aria-label={pinned ? `Unpin ${laneId(lane)}` : `Pin ${laneId(lane)} to the top`}
            title={pinned ? 'Pinned to the top' : 'Pin to the top'}
            onClick={onPin}
            style={{
              ...btn,
              padding: '6px 10px',
              fontSize: 18,
              lineHeight: 1,
              color: pinned ? 'var(--wr-signal)' : 'var(--wr-muted)',
            }}
          >
            {pinned ? '★' : '☆'}
          </button>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 600 }}>
              {flagFor(lane.pol)} {polName} → {flagFor(lane.pod)} {podName}
            </div>
            <div style={{ ...label, marginTop: 4 }}>
              {lane.pol} → {lane.pod}
              {fresh ? ` · ${fresh}` : ''}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
          {view?.sum.best != null && (
            <div style={{ textAlign: 'right' }}>
              <div style={label}>Best {boxLabel}</div>
              <div style={{ fontFamily: mono, fontSize: 24, fontWeight: 700 }}>
                {fmtPrice(view.sum.best)}
              </div>
              <div style={{ fontSize: 12, color: 'var(--wr-ink2)' }}>{view.sum.bestCarrier}</div>
            </div>
          )}
          {view && view.series.length > 1 && (
            <div style={{ textAlign: 'right', width: 170 }}>
              <Line
                values={view.series.map((p) => p.value)}
                color={trendColor(change)}
                w={160}
                h={40}
                label={`Rate trend ${laneId(lane)}`}
              />
              <div
                style={{
                  fontFamily: mono,
                  fontSize: 12,
                  fontWeight: 600,
                  color: trendColor(change),
                }}
              >
                {change == null ? '' : `${pct(change)} over ${view.series.length} days`}
              </div>
            </div>
          )}
          <button
            type="button"
            style={btn}
            onClick={onRemove}
            aria-label={`Remove ${laneId(lane)}`}
          >
            Remove
          </button>
        </div>
      </header>

      {load.state === 'loading' && (
        <div style={{ padding: 24, color: 'var(--wr-muted)', fontSize: 14 }}>Loading rates…</div>
      )}
      {load.state === 'error' && (
        <div style={{ padding: 24, color: 'var(--wr-bad)', fontSize: 14 }}>
          Could not load this lane. Try refresh in a moment.
        </div>
      )}
      {view && view.offers.length === 0 && (
        <div style={{ padding: 24, color: 'var(--wr-ink2)', fontSize: 14 }}>
          {view.liners.length > 0
            ? 'Every liner on this lane is hidden. Bring some back with Liners.'
            : view.sailingsTotal === 0
              ? 'No bookable sailings on this lane right now.'
              : 'No liner shows a price on this lane right now.'}
        </div>
      )}
      {view && view.offers.length > 0 && (
        <>
          <SpreadBand view={view} boxLabel={boxLabel} />
          <BoxRatioNote offers={view.offers} />
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
              <thead>
                <tr>
                  <th style={th}>Liner</th>
                  {priceCols.map((b) => (
                    <th
                      key={b.id}
                      style={{ ...th, color: b.id === box ? 'var(--wr-signal)' : undefined }}
                      title={b.id === box ? 'Liners are ranked by this container' : undefined}
                    >
                      {b.label}
                      {b.id === box ? ' ●' : ''}
                    </th>
                  ))}
                  {cols.has('gap') && <th style={th}>vs best</th>}
                  {cols.has('transit') && <th style={th}>Transit</th>}
                  {cols.has('next') && <th style={th}>Next sailing</th>}
                  {cols.has('vessel') && <th style={th}>Vessel</th>}
                  {cols.has('sailings') && <th style={th}>Sailings</th>}
                  {hasOnTime && <th style={th}>On-time</th>}
                </tr>
              </thead>
              <tbody>
                {view.offers.map((o) => {
                  const via = transshipPorts(
                    o.cheapest.raw?.transferList,
                    lane.polName,
                    lane.podName,
                    polName,
                    podName,
                    lane.pol,
                    lane.pod
                  ).join(', ')
                  const ot = view.onTime.get(o.carrier.toUpperCase())
                  const isBest = o.price != null && o.price === view.sum.best
                  return (
                    <tr key={o.carrier}>
                      <td style={td}>
                        <CarrierChip code={o.carrier} />
                      </td>
                      {priceCols.map((b) => {
                        const v = o.prices[b.id]
                        const low = v != null && v === lowest(b.id) && view.offers.length > 1
                        return (
                          <td
                            key={b.id}
                            style={{
                              ...td,
                              fontFamily: mono,
                              fontWeight: b.id === box ? 700 : 500,
                              color:
                                v == null
                                  ? 'var(--wr-muted)'
                                  : low
                                    ? 'var(--wr-good)'
                                    : 'var(--wr-ink)',
                            }}
                          >
                            {v == null ? '-' : fmtPrice(v)}
                            {b.id === box && isBest && view.offers.length > 1 && (
                              <span
                                style={{ marginLeft: 8, fontSize: 11, color: 'var(--wr-good)' }}
                              >
                                lowest
                              </span>
                            )}
                          </td>
                        )
                      })}
                      {cols.has('gap') && (
                        <td style={{ ...td, fontFamily: mono, color: 'var(--wr-ink2)' }}>
                          {o.price == null || view.sum.best == null || o.price === view.sum.best
                            ? '-'
                            : `+${fmtPrice(o.price - view.sum.best)}`}
                        </td>
                      )}
                      {cols.has('transit') && (
                        <td style={{ ...td, fontFamily: mono, color: 'var(--wr-ink2)' }}>
                          {o.cheapest.transit_days != null ? `${o.cheapest.transit_days}d` : '-'}
                        </td>
                      )}
                      {cols.has('next') && (
                        <td style={{ ...td, fontFamily: mono, color: 'var(--wr-ink2)' }}>
                          {shortDate(o.nextEtd)}
                        </td>
                      )}
                      {cols.has('vessel') && (
                        <td style={{ ...td, color: 'var(--wr-ink2)', fontSize: 13 }}>
                          {o.cheapest.vessel ?? '-'}
                          {o.cheapest.voyage ? ` ${o.cheapest.voyage}` : ''}
                          {via ? (
                            <div style={{ fontSize: 12, color: 'var(--wr-muted)' }}>via {via}</div>
                          ) : null}
                        </td>
                      )}
                      {cols.has('sailings') && (
                        <td style={{ ...td, fontFamily: mono, color: 'var(--wr-ink2)' }}>
                          {o.sailings}
                        </td>
                      )}
                      {hasOnTime && (
                        <td
                          style={{
                            ...td,
                            fontFamily: mono,
                            color: !ot
                              ? 'var(--wr-muted)'
                              : ot.observations < THIN_SAMPLE
                                ? 'var(--wr-muted)'
                                : 'var(--wr-ink)',
                          }}
                          title={
                            ot
                              ? `${ot.observations} sailings observed${ot.observations < THIN_SAMPLE ? ' (small sample)' : ''}`
                              : undefined
                          }
                        >
                          {ot ? `${Math.round(ot.pct)}%` : '-'}
                          {ot && (
                            <span style={{ fontSize: 11, color: 'var(--wr-muted)' }}>
                              {' '}
                              ({ot.observations})
                            </span>
                          )}
                        </td>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 12,
              justifyContent: 'flex-end',
              alignItems: 'center',
              padding: '12px 20px',
            }}
          >
            <button type="button" style={btn} onClick={() => setAll((v) => !v)}>
              {all ? 'Hide sailings' : 'All sailings'}
            </button>
          </div>
          {all && (
            <div style={{ overflowX: 'auto', borderTop: '1px solid var(--wr-line)' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
                <thead>
                  <tr>
                    <th style={th}>Liner</th>
                    {priceCols.map((b) => (
                      <th key={b.id} style={th}>
                        {b.label}
                      </th>
                    ))}
                    <th style={th}>ETD</th>
                    <th style={th}>ETA</th>
                    <th style={th}>Transit</th>
                    <th style={th}>Vessel</th>
                  </tr>
                </thead>
                <tbody>
                  {view.priced.slice(0, 60).map((s, i) => (
                    <tr key={`${s.carrier}-${s.vessel}-${s.voyage}-${s.etd}-${i}`}>
                      <td style={td}>
                        <CarrierChip code={(s.carrier ?? '').trim() || 'Unknown'} />
                      </td>
                      {priceCols.map((b) => (
                        <td
                          key={b.id}
                          style={{ ...td, fontFamily: mono, fontWeight: b.id === box ? 700 : 500 }}
                        >
                          {boxPrice(s, b.id) == null ? '-' : fmtPrice(boxPrice(s, b.id))}
                        </td>
                      ))}
                      <td style={{ ...td, fontFamily: mono }}>{shortDate(s.etd)}</td>
                      <td style={{ ...td, fontFamily: mono }}>{shortDate(s.eta)}</td>
                      <td style={{ ...td, fontFamily: mono }}>
                        {s.transit_days != null ? `${s.transit_days}d` : '-'}
                      </td>
                      <td style={{ ...td, fontSize: 13, color: 'var(--wr-ink2)' }}>
                        {s.vessel ?? '-'}
                        {s.voyage ? ` ${s.voyage}` : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {view.priced.length > 60 && (
                <div style={{ padding: '10px 20px', fontSize: 12, color: 'var(--wr-muted)' }}>
                  Showing the 60 cheapest of {view.priced.length}.
                </div>
              )}
            </div>
          )}
        </>
      )}
      <SpotPanel pol={lane.pol} pod={lane.pod} watch={watch} tick={tick} />
    </section>
  )
}

function AddLane({
  pols,
  pods,
  have,
  onAdd,
}: {
  pols: ReturnType<typeof buildPortOptions>
  pods: ReturnType<typeof buildPortOptions>
  have: Set<string>
  onAdd: (l: FavLane) => void
}) {
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [msg, setMsg] = useState<string | null>(null)

  function submit(e: React.FormEvent) {
    e.preventDefault()
    const pol = resolvePort(from, pols)
    const pod = resolvePort(to, pods)
    if (!pol) return setMsg('Pick the origin port from the list.')
    if (!pod) return setMsg('Pick the destination port from the list.')
    if (pol === pod) return setMsg('Origin and destination are the same port.')
    if (have.has(`${pol}-${pod}`)) return setMsg('That lane is already on your board.')
    onAdd({
      pol,
      pod,
      polName: pols.find((p) => p.code === pol)?.name,
      podName: pods.find((p) => p.code === pod)?.name,
    })
    setFrom('')
    setTo('')
    setMsg(null)
  }

  return (
    <form
      onSubmit={submit}
      style={{
        ...card,
        padding: 16,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 12,
        alignItems: 'end',
      }}
    >
      <div style={{ flex: '1 1 220px' }}>
        <label style={label} htmlFor="wr-from">
          From
        </label>
        <input
          id="wr-from"
          list="wr-pols"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          placeholder="Shenzhen"
          autoComplete="off"
          style={{ ...input, marginTop: 6 }}
        />
        <datalist id="wr-pols">
          {pols.slice(0, 300).map((p) => (
            <option key={p.code} value={portLabel(p)} />
          ))}
        </datalist>
      </div>
      <div style={{ flex: '1 1 220px' }}>
        <label style={label} htmlFor="wr-to">
          To
        </label>
        <input
          id="wr-to"
          list="wr-pods"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder="Manzanillo"
          autoComplete="off"
          style={{ ...input, marginTop: 6 }}
        />
        <datalist id="wr-pods">
          {pods.slice(0, 300).map((p) => (
            <option key={p.code} value={portLabel(p)} />
          ))}
        </datalist>
      </div>
      <button type="submit" style={btnPrimary}>
        Add lane
      </button>
      {msg && (
        <div role="alert" style={{ flexBasis: '100%', fontSize: 13, color: 'var(--wr-bad)' }}>
          {msg}
        </div>
      )}
    </form>
  )
}

// ── The board ───────────────────────────────────────────────────────────────────────────────────

export function LanesBoard({
  heading = true,
  embedded = false,
}: { heading?: boolean; embedded?: boolean }) {
  const favs = useFavs()
  const box = toBox(useBox())
  const sort = toSort(useLaneSort())
  const colsRaw = useCols()
  const hiddenRaw = useHiddenLiners()
  const cols = useMemo(() => new Set(toColumns(colsRaw)), [colsRaw])
  const hidden = useMemo(() => toHidden(hiddenRaw), [hiddenRaw])
  const pinnedList = usePinned()
  const pinned = useMemo(() => new Set(pinnedList), [pinnedList])
  const [feed, setFeed] = useState<LaneRow[]>([])
  const [tick, setTick] = useState(0)

  useEffect(() => {
    let live = true
    getLanes()
      .then((l) => live && setFeed(l))
      // The picker still works from the seed list when the feed is slow or down.
      .catch(() => {})
    return () => {
      live = false
    }
  }, [])

  // A lane saved under an alias (Shenzhen, CNSZX) is moved to the port that has the rates (Yantian).
  useEffect(() => {
    for (const f of favs) {
      const pol = canonicalPort(f.pol)
      const pod = canonicalPort(f.pod)
      if (pol === f.pol && pod === f.pod) continue
      toggleFav(f)
      if (pol !== pod && !favs.some((x) => laneId(x) === `${pol}-${pod}`))
        toggleFav({
          pol,
          pod,
          polName: pol !== f.pol ? PORT_ALIASES[f.pol]?.name : f.polName,
          podName: pod !== f.pod ? PORT_ALIASES[f.pod]?.name : f.podName,
        })
    }
  }, [favs])

  const loads = useLaneLoads(favs, tick)

  // Tell the backend which lanes this customer is looking at, so the daily job fetches their spot rates. Once
  // per lane per page load; the call is idempotent and never blocks the board.
  const [watched, setWatched] = useState<Record<string, WatchState | null>>({})
  const watchKey = favs.map(laneId).join(',')
  // biome-ignore lint/correctness/useExhaustiveDependencies: watchKey stands for the lane list
  useEffect(() => {
    for (const f of favs) {
      const id = laneId(f)
      // A lane saved under an alias (Shenzhen, CNSZX) is moved to its real port by the effect above. Register the
      // real port, never the alias, or the daily job would fetch the same lane twice under two codes.
      if (canonicalPort(f.pol) !== f.pol || canonicalPort(f.pod) !== f.pod) continue
      if (watchedThisLoad.has(id)) continue
      watchedThisLoad.add(id)
      void watchLane(f.pol, f.pod).then((w) => setWatched((cur) => ({ ...cur, [id]: w })))
    }
  }, [watchKey])
  const pols = useMemo(() => buildPortOptions(feed, 'pol', SEED_ORIGINS), [feed])
  const pods = useMemo(() => buildPortOptions(feed, 'pod', SEED_DESTINATIONS), [feed])
  const have = useMemo(() => new Set(favs.map(laneId)), [favs])

  const views = useMemo(() => {
    const m = new Map<string, LaneView | null>()
    for (const l of favs)
      m.set(laneId(l), laneView(loads[laneId(l)] ?? { state: 'loading' }, box, hidden))
    return m
  }, [favs, loads, box, hidden])

  const metrics = useMemo(() => {
    const m = new Map<string, LaneMetrics>()
    for (const [id, v] of views)
      if (v) m.set(id, { best: v.sum.best, change: trendPct(v.series.map((p) => p.value)) })
    return m
  }, [views])

  const ordered = useMemo(
    () =>
      sortLanes(
        favs.map((f) => ({ id: laneId(f), name: laneName(f), lane: f })),
        pinned,
        sort,
        metrics
      ).map((x) => x.lane),
    [favs, pinned, sort, metrics]
  )

  const allLiners = useMemo(() => {
    const s = new Set<string>()
    for (const v of views.values()) for (const l of v?.liners ?? []) s.add(l)
    return [...s].sort((a, b) => a.localeCompare(b))
  }, [views])

  const index = useMemo(() => basketIndex([...views.values()].map((v) => v?.series ?? [])), [views])
  const stats = useMemo(() => {
    const rows = favs.flatMap((f) => {
      const id = laneId(f)
      const m = metrics.get(id)
      return m ? [{ name: laneName(f), ...m }] : []
    })
    const priced = rows.filter((r) => r.best != null) as Array<{
      name: string
      best: number
      change: number | null
    }>
    const moved = rows.filter((r) => r.change != null) as Array<{
      name: string
      best: number | null
      change: number
    }>
    const cheapest = priced.length ? priced.reduce((a, b) => (b.best < a.best ? b : a)) : null
    const riser = moved.filter((r) => r.change > 0.05).sort((a, b) => b.change - a.change)[0]
    const faller = moved.filter((r) => r.change < -0.05).sort((a, b) => a.change - b.change)[0]
    return {
      avg: priced.length ? priced.reduce((s, r) => s + r.best, 0) / priced.length : null,
      cheapest: cheapest ? `${cheapest.name} (${fmtPrice(cheapest.best)})` : null,
      riser: riser ? `${riser.name} (${pct(riser.change)})` : null,
      faller: faller ? `${faller.name} (${pct(faller.change)})` : null,
      lanes: favs.length,
    }
  }, [favs, metrics])

  const suggestions = useMemo(() => {
    const seen = new Set<string>()
    const out: FavLane[] = []
    for (const l of feed) {
      const id = `${l.pol_code}-${l.pod_code}`
      if (seen.has(id) || !l.pol_name || !l.pod_name) continue
      seen.add(id)
      out.push({ pol: l.pol_code, pod: l.pod_code, polName: l.pol_name, podName: l.pod_name })
      if (out.length === 6) break
    }
    return out
  }, [feed])

  const boxLabel = BOXES.find((b) => b.id === box)?.label ?? ''

  function exportCsv() {
    const rows: Array<Array<string | number | null>> = []
    for (const f of ordered) {
      const v = views.get(laneId(f))
      if (!v) continue
      rows.push(
        ...laneCsvRows(
          {
            pol: f.pol,
            pod: f.pod,
            polName: displayPort(f.pol, f.polName),
            podName: displayPort(f.pod, f.podName),
          },
          v.offers,
          v.onTime,
          box,
          v.sum.best
        )
      )
    }
    const blob = new Blob([toCsv([CSV_HEADER, ...rows])], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `my-lanes-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div
      style={{
        ...tokens,
        maxWidth: embedded ? 'none' : 1120,
        margin: '0 auto',
        padding: embedded ? '0' : '32px 24px 56px',
        color: 'var(--wr-ink)',
      }}
    >
      {heading && (
        <>
          <div style={{ ...label, color: 'var(--wr-signal)', letterSpacing: '0.16em' }}>
            Freight rates
          </div>
          <h1
            style={{
              fontFamily: 'var(--font-display, inherit)',
              fontSize: 30,
              fontWeight: 600,
              letterSpacing: '-0.03em',
              margin: '10px 0 4px',
            }}
          >
            My lanes
          </h1>
          <p style={{ fontSize: 14, color: 'var(--wr-ink2)', margin: 0 }}>
            Add the lanes you ship and compare every liner's rate on each. Pin the ones that matter,
            choose what to see, and export it. Your choices are saved in this browser.
          </p>
        </>
      )}

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          alignItems: 'center',
          margin: '20px 0 12px',
        }}
      >
        <fieldset
          style={{
            display: 'flex',
            gap: 6,
            border: 0,
            padding: 0,
            margin: 0,
            alignItems: 'center',
          }}
        >
          <legend style={sr}>Rank liners by container</legend>
          <span style={label} aria-hidden="true">
            Rank by
          </span>
          {BOXES.map((b) => (
            <button
              key={b.id}
              type="button"
              aria-pressed={box === b.id}
              onClick={() => setBox(b.id)}
              style={box === b.id ? btnPrimary : btn}
            >
              {b.label}
            </button>
          ))}
        </fieldset>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={label}>Sort</span>
          <select
            value={sort}
            onChange={(e) => setLaneSort(e.target.value)}
            style={{ ...input, width: 'auto', padding: '8px 10px' }}
          >
            {SORTS.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
        <Menu title="Columns">
          {COLUMNS.map((c) => (
            <Check
              key={c.id}
              checked={cols.has(c.id)}
              onChange={(on) =>
                setCols(COLUMNS.map((x) => x.id).filter((id) => (id === c.id ? on : cols.has(id))))
              }
            >
              {c.label}
            </Check>
          ))}
          <button type="button" style={btn} onClick={() => setCols(COLUMNS.map((c) => c.id))}>
            Show all
          </button>
        </Menu>
        <Menu title="Liners">
          {allLiners.length === 0 && (
            <span style={{ fontSize: 13, color: 'var(--wr-muted)' }}>Loading liners…</span>
          )}
          {allLiners.map((l) => (
            <Check
              key={l}
              checked={!hidden.has(l)}
              onChange={(on) =>
                setHiddenLiners(on ? [...hidden].filter((x) => x !== l) : [...hidden, l])
              }
            >
              <CarrierChip code={l} />
            </Check>
          ))}
          {hidden.size > 0 && (
            <button type="button" style={btn} onClick={() => setHiddenLiners([])}>
              Show all liners
            </button>
          )}
        </Menu>
        <span style={{ ...label, marginLeft: 'auto' }}>
          {favs.length} {favs.length === 1 ? 'lane' : 'lanes'}
        </span>
        <button type="button" style={btn} onClick={exportCsv} disabled={favs.length === 0}>
          Export CSV
        </button>
        <button type="button" style={btn} onClick={() => setTick((t) => t + 1)}>
          Refresh
        </button>
      </div>

      <AddLane pols={pols} pods={pods} have={have} onAdd={(l) => toggleFav(l)} />

      {favs.length > 0 && (
        <IndexPanel points={index} boxLabel={boxLabel} stats={stats} noHistory={box === '40gp'} />
      )}

      <div style={{ display: 'grid', gap: 16, marginTop: 16 }}>
        {favs.length === 0 && (
          <div style={{ ...card, padding: 24 }}>
            <div style={{ fontSize: 16, fontWeight: 600 }}>No lanes yet</div>
            <p style={{ fontSize: 14, color: 'var(--wr-ink2)', margin: '6px 0 14px' }}>
              Pick an origin and a destination above, or start from one of these.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {suggestions.map((s) => (
                <button key={laneId(s)} type="button" style={btn} onClick={() => toggleFav(s)}>
                  {laneName(s)}
                </button>
              ))}
            </div>
          </div>
        )}
        {ordered.map((l) => (
          <LaneCard
            key={laneId(l)}
            lane={l}
            box={box}
            load={loads[laneId(l)] ?? { state: 'loading' }}
            view={views.get(laneId(l)) ?? null}
            cols={cols}
            pinned={pinned.has(laneId(l))}
            onPin={() => togglePinned(laneId(l))}
            onRemove={() => {
              if (pinned.has(laneId(l))) togglePinned(laneId(l))
              toggleFav(l)
            }}
            watch={watched[laneId(l)] ?? null}
            tick={tick}
          />
        ))}
      </div>

      {heading && <UsagePanel />}

      <p style={{ fontSize: 12, color: 'var(--wr-muted)', marginTop: 20, lineHeight: 1.6 }}>
        Indicative market rates from public liner cards, refreshed daily. Not a firm quote:
        surcharges and space vary by booking. Use a booking request for a firm price.
      </p>
    </div>
  )
}
